<template>
  <div class="container">
    <div class="map-container">
      <div v-if="loading" class="spinner-container">
        <ProgressSpinner />
      </div>
      <CustomMap v-else :dots="dots" :center="center" @on-click-marker="onClickFacility" />
    </div>

    <Dialog v-model:visible="showDialog" modal :style="{ width: '1800px' }" @hide="onCloseDialog"
      :data-testid="`facility-dialog-map`">
      <template #header>
        <div class="dialog-title">
          {{ selectedFacility?.facility?.facility_name }}
        </div>
      </template>
      <div class="dialog-content">
        <FacilityForm v-if="selectedFacility && showDialog" />
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, defineProps } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import CustomMap from "./CustomMap.vue";
import FacilityForm from "../FacilityForm.vue";
import { useMonitoringStructure } from "../structure/useMonitoringStructure";
import { ICON_MAPPINGS, GROUP_COLORS } from "./mapConstants";

const props = defineProps({
  facilityType: {
    type: Array,
    default: () => []
  },
  facilityTypes: {
    type: Array
  }
});

const { monitoringStruct } = useMonitoringStructure();
const store = useStore();
const route = useRoute();
const router = useRouter();

const selectedFacility = ref(null);
const showDialog = ref(false);
const loading = ref(false);
const intervalTimer = ref(null);
const firstOpen = ref(false);
const intervalTimerCenter = ref(null);
const center = ref([54.3604273, 53.2809674]);

const facility = computed(() => store.getters.facility);

const facilitiesTemp = computed(() => {
  if (!facility.value) return [];
  return facility.value.filter(item =>
    item.facility_actual &&
    item.facility_coord_lng &&
    item.facility_coord_ltd
  );
});

const filteredFacilities = computed(() => {
  if (!props.facilityType?.length) return facilitiesTemp.value;
  return facilitiesTemp.value.filter(facility =>
    props.facilityType.includes(facility.facility_type_id)
  );
});

const dotsTemp = computed(() => {
  return filteredFacilities.value.map(facility => ({
    facility,
    id: facility.facility_id,
    name: facility.facility_name,
    icon: ICON_MAPPINGS.well,
    coord: [
      facility.facility_coord_ltd,
      facility.facility_coord_lng,
    ],
  }));
});

const monitoringMap = computed(() => {
  const map = new Map();
  monitoringStruct.value.forEach(item => {
    map.set(item.facilityId, item);
  });
  return map;
});

const dots = computed(() => {
  checkOpenFacility();
  return dotsTemp.value.map(dot => {
    const monitoring = monitoringMap.value.get(dot.id);

    if (monitoring) {
      const iconType = ICON_MAPPINGS[dot.facility.facility_type_class] || ICON_MAPPINGS.well;
      return {
        ...dot,
        icon: iconType.replace('{0}', getColor(monitoring.groupId)),
        facility: {
          ...dot.facility,
          activeEvent: monitoring.activeEvent,
          groupId: monitoring.groupId,
          activeEventsHint: monitoring.activeEventsHint
        }
      };
    } else {
      return {
        ...dot,
        icon: ICON_MAPPINGS.well.replace('{0}', 'yellow'),
        facility: {
          ...dot.facility,
          groupId: 2,
          activeEvent: "Соединение отсутствует"
        }
      };
    }
  });
});

watch(dots, () => {
  checkOpenFacility();
  intervalTimerCenter.value = setTimeout(() => {
    center.value = dotsTemp.value?.length
      ? dotsTemp.value[0]?.coord
      : center.value;
  }, 250);
});

function checkOpenFacility() {
  if (!dots.value || firstOpen.value) return;
  if (route.params.facility_id && route.params.node_type_id && route.params.facility_type_id) {
    const dotFacility = dots.value.find(item => Number(route.params.facility_id) === item.id);
    if (dotFacility) {
      firstOpen.value = true;
      selectedFacility.value = dotFacility;
      intervalTimer.value = setTimeout(() => {
        showDialog.value = true;
      }, 200);
    }
  } else {
    firstOpen.value = true;
  }
}

function getColor(groupId) {
  return GROUP_COLORS[groupId] || GROUP_COLORS.default;
}

function getStyleInput(groupId) {
  return {
    "background-color": getColor(groupId),
    color: "black"
  };
}

function onClickFacility(facility) {
  const facilityType = props.facilityTypes.find(
    item => facility.facility?.facility_type_id === item.facility_type_id
  );

  if (facilityType) {
    const params = {
      node_type_id: facilityType.node_type_id,
      facility_id: facility.id,
      facility_type_id: facility.facility?.facility_type_id
    };
    router.replace({
      name: 'facility-map-child',
      params
    });
    selectedFacility.value = facility;
    intervalTimer.value = setTimeout(() => {
      showDialog.value = true;
    }, 200);
  }
}

function onCloseDialog() {
  selectedFacility.value = null;
  router.replace({
    name: 'facility-map-child',
    params: {
      node_type_id: 0,
      facility_id: 0,
      facility_type_id: 0
    }
  });
}

onUnmounted(() => {
  clearTimeout(intervalTimer.value);
  clearTimeout(intervalTimerCenter.value);
});
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  width: 100%;
}

.map-container {
  height: 820px;
  width: 100%;
  padding: 5px;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.dialog-title {
  font-weight: bold;
  text-align: center;
  font-size: 1.2rem;
}

.dialog-content {
  min-height: 900px;
}

.component-disabled {
  pointer-events: none;
}

.component-width {
  width: 100%;
}
</style>